<template>
  <DialogPrime v-model:visible="localVisible" modal maximizable :draggable="false" class="mapping-ref-dialog"
    :style="{ width: 'min(1200px, 96vw)' }" :breakpoints="{ '768px': '100vw' }" @show="onShow">
    <template #header>
      <div class="d-flex align-items-center gap-3">
        <span class="mapping-ref-icon"><i class="pi pi-sitemap"></i></span>
        <div>
          <h6 class="mb-0 fw-bold text-dark">Data Mapping Reference</h6>
          <small class="text-muted">{{ company }} &middot; values filled automatically in the form (read only)</small>
        </div>
      </div>
    </template>

    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab v-for="tab in MAPPING_TABS" :key="tab.key" :value="tab.key">
          <span class="d-inline-flex align-items-center gap-2">
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
            <span v-if="state[tab.key].loaded" class="mapping-ref-count">{{ state[tab.key].rows.length }}</span>
          </span>
        </Tab>
      </TabList>

      <TabPanels class="px-0 pb-0">
        <TabPanel v-for="tab in MAPPING_TABS" :key="tab.key" :value="tab.key">
          <div class="card mapping-ref-card">
            <div class="card-body p-3">
              <!-- Cara membaca mapping: kunci source -> nilai target -->
              <div class="mapping-ref-explain mb-3">
                <div class="d-flex flex-wrap align-items-center gap-2">
                  <span class="mapping-ref-label">Source</span>
                  <template v-for="(part, i) in tab.sourceParts" :key="part">
                    <span class="mapping-ref-chip is-source">{{ partLabel(part) }}</span>
                    <span v-if="i < tab.sourceParts.length - 1" class="text-muted">+</span>
                  </template>
                  <i class="pi pi-arrow-right mx-1 text-muted"></i>
                  <span class="mapping-ref-label">Target</span>
                  <span v-for="target in targetColumns(tab)" :key="target" class="mapping-ref-chip is-target">{{ partLabel(target) }}</span>
                </div>
                <div class="small text-muted mt-2">{{ tab.description }}</div>
              </div>

              <!-- Pencarian & filter -->
              <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
                <IconField class="mapping-ref-search">
                  <InputIcon class="pi pi-search" />
                  <InputText v-model="state[tab.key].search" size="small" placeholder="Search source or target..." fluid />
                </IconField>
                <MultiSelect v-for="part in tab.filterParts" :key="part" v-model="state[tab.key].filters[part]"
                  :options="distinctValues(tab, part)" :placeholder="`All ${partLabel(part)}`" filter
                  :max-selected-labels="2" size="small" class="mapping-ref-filter" />
                <SelectButton v-if="tab.view === 'matrix'" v-model="state[tab.key].mode" :options="VIEW_MODES"
                  option-label="label" option-value="value" :allow-empty="false" size="small" />
                <ButtonPrime v-if="hasActiveFilter(tab)" label="Reset" icon="pi pi-filter-slash" text size="small" @click="resetFilters(tab)" />
                <span class="ms-auto small text-muted">
                  Showing <strong>{{ filteredRows(tab).length }}</strong> of {{ state[tab.key].rows.length }}
                </span>
                <ButtonPrime v-tooltip.top="'Reload'" icon="pi pi-refresh" text rounded size="small"
                  :loading="state[tab.key].loading" @click="load(tab, true)" />
              </div>

              <div v-if="contextMaterialType && state[tab.key].filters.material_type?.includes(contextMaterialType)" class="mapping-ref-context mb-3">
                <i class="pi pi-info-circle"></i>
                Filtered by the material type of the selected item (<strong>{{ contextMaterialType }}</strong>).
              </div>

              <!-- Loading / error / kosong -->
              <div v-if="state[tab.key].loading && !state[tab.key].loaded">
                <Skeleton v-for="n in 6" :key="n" height="2rem" class="mb-2" />
              </div>
              <div v-else-if="state[tab.key].error" class="mapping-ref-empty">
                <i class="pi pi-exclamation-triangle"></i>
                <div>Failed to load the mapping data.</div>
                <ButtonPrime label="Try again" size="small" outlined @click="load(tab, true)" />
              </div>
              <div v-else-if="!filteredRows(tab).length" class="mapping-ref-empty">
                <i class="pi pi-inbox"></i>
                <div>{{ state[tab.key].rows.length ? 'No mapping matches the filter.' : 'No mapping data yet.' }}</div>
              </div>

              <!-- Matriks Material Type x Plant (Item Category) -->
              <div v-else-if="tab.view === 'matrix' && state[tab.key].mode === 'matrix'" class="mapping-ref-matrix-wrap">
                <table class="mapping-ref-matrix">
                  <thead>
                    <tr>
                      <th class="is-corner">{{ partLabel(tab.sourceParts[0]) }} \ {{ partLabel(tab.sourceParts[1]) }}</th>
                      <th v-for="col in matrix(tab).cols" :key="col">{{ col }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in matrix(tab).rows" :key="row">
                      <th>{{ row }}</th>
                      <td v-for="col in matrix(tab).cols" :key="col">
                        <div v-if="matrix(tab).cells[`${row}|${col}`]" class="d-flex flex-wrap gap-1">
                          <span v-for="value in matrix(tab).cells[`${row}|${col}`]" :key="value" class="mapping-ref-pill"
                            :style="pillStyle(value)">{{ value }}</span>
                        </div>
                        <span v-else class="text-muted">&ndash;</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Tabel source -> target -->
              <DataTable v-else :value="filteredRows(tab)" size="small" striped-rows scrollable scroll-height="50vh"
                :paginator="filteredRows(tab).length > 25" :rows="25" :rows-per-page-options="[25, 50, 100]"
                removable-sort sort-mode="single" class="mapping-ref-table">
                <Column v-for="part in tab.sourceParts" :key="part" :field="part" :header="partLabel(part)" sortable
                  header-class="is-source" :style="{ minWidth: '130px' }">
                  <template #body="{ data }">
                    <span class="fw-semibold">{{ data[part] || '–' }}</span>
                  </template>
                </Column>
                <Column v-for="target in targetColumns(tab)" :key="target" :field="target" :header="partLabel(target)"
                  sortable header-class="is-target" body-class="is-target-body" :style="{ minWidth: '130px' }">
                  <template #body="{ data }">
                    <div v-if="Array.isArray(data[target])" class="d-flex flex-wrap gap-1">
                      <span v-for="value in data[target]" :key="value" class="mapping-ref-pill" :style="pillStyle(value)">{{ value }}</span>
                    </div>
                    <span v-else>{{ data[target] || '–' }}</span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <template #footer>
      <ButtonPrime label="Close" size="small" @click="localVisible = false" />
    </template>
  </DialogPrime>
</template>

<script>
import api from "@/helpers/api";
import formConfigService from "@/services/materialCatalogingRequest/formConfigService";

// Mapping ConfigDataMapping yang dipakai otomasi form (FORM01/FORM02 INALUM). `sourceParts` = urutan bagian kunci `source`
// (dipisah "_"), `targets` = key di `target` (object). Target berupa daftar (SPAREPART_CATEGORY) -> kolom `options`.
const MAPPING_TABS = [
  {
    key: "SPAREPART_CATEGORY",
    label: "Item Category",
    icon: "pi pi-th-large",
    view: "matrix",
    sourceParts: ["material_type", "plant"],
    filterParts: ["material_type", "plant"],
    description: "Item Category options per Material Type and Plant. A single option is selected automatically."
  },
  {
    key: "MRP_TYPE_AND_LOT_SIZE",
    label: "MRP Type & Lot Size",
    icon: "pi pi-sliders-h",
    view: "table",
    sourceParts: ["sparepart_category"],
    filterParts: [],
    targets: ["mrp_type", "lot_size"],
    description: "MRP Type and Lot Size filled in right after a Item Category is chosen."
  },
  {
    key: "PROFIT_CENTER_CONFIG",
    label: "Profit Center",
    icon: "pi pi-building",
    view: "table",
    sourceParts: ["material_type", "plant", "valuation_class"],
    filterParts: ["material_type", "plant"],
    targets: ["profit_center_code"],
    description: "Profit Center of the General Plant Data row, from Material Type, its Plant and the Valuation Class (Accounting tab)."
  },
  {
    key: "MRP_CONFIG",
    label: "MRP Config",
    icon: "pi pi-cog",
    view: "table",
    sourceParts: ["material_type", "material_group", "plant"],
    filterParts: ["material_type", "material_group", "plant"],
    targets: ["purchasing_group", "mrp_group", "mrp_controller", "storage_location_ep", "procurement_type"],
    description: "MRP and Purchasing values filled per plant row from Material Type, Material Group and Plant."
  }
];

const PART_LABELS = {
  material_type: "Material Type",
  material_group: "Material Group",
  plant: "Plant",
  valuation_class: "Valuation Class",
  sparepart_category: "Item Category",
  options: "Item Category Options",
  mrp_type: "MRP Type",
  lot_size: "Lot Size",
  profit_center_code: "Profit Center",
  purchasing_group: "Purchasing Group",
  mrp_group: "MRP Group",
  mrp_controller: "MRP Controller",
  storage_location_ep: "Sloc for EP",
  procurement_type: "Procurement Type"
};

// Bentuk tiap bagian kunci `source` (dicek berurutan): Material Type = "Z" + 3 huruf (ZSPR, ZSUP, ZRAW, ZNNV, ZNOV, ZSFG, ZFGD),
// Material Group = 6 angka (050103), Valuation Class = "Z" + 3 angka (Z744). Selain itu dianggap Plant (A001, A0N1, ...).
const SOURCE_PART_PATTERNS = {
  material_type: /^Z[A-Z]{3}$/,
  material_group: /^\d{6}$/,
  valuation_class: /^Z\d{3}$/
};

const VIEW_MODES = [
  { label: "Matrix", value: "matrix" },
  { label: "List", value: "list" }
];

// Warna pill stabil per nilai (nilai sama = warna sama di seluruh modal).
const PILL_COLORS = [
  ["#e8f1fb", "#185ea2"], ["#e9f7ef", "#1e7b45"], ["#fff4e5", "#a35a00"],
  ["#f3ecfb", "#6b3fa0"], ["#fdecec", "#b42318"], ["#eef2f6", "#475467"]
];

const emptyState = (tab) => ({
  rows: [],
  loading: false,
  loaded: false,
  error: false,
  search: "",
  mode: "matrix",
  filters: Object.fromEntries(tab.filterParts.map((part) => [part, []]))
});

export default {
  name: "MappingReferenceModal",
  props: {
    visible: { type: Boolean, default: false },
    // Company MCR (mapping ConfigDataMapping per company, mis. INALUM).
    company: { type: String, required: true },
    // Material Type item yang sedang dipilih -- dipakai sebagai filter awal (bisa dihapus user).
    contextMaterialType: { type: String, default: "" }
  },
  emits: ["update:visible"],
  data() {
    return {
      MAPPING_TABS,
      VIEW_MODES,
      activeTab: MAPPING_TABS[0].key,
      state: Object.fromEntries(MAPPING_TABS.map((tab) => [tab.key, emptyState(tab)]))
    };
  },
  computed: {
    localVisible: {
      get() { return this.visible; },
      set(value) { this.$emit("update:visible", value); }
    }
  },
  watch: {
    // Data per tab dimuat saat tab pertama kali dibuka (bukan semua sekaligus -- MRP_CONFIG ribuan baris).
    activeTab() {
      if (this.visible) this.load(this.currentTab());
    },
    company() {
      this.state = Object.fromEntries(MAPPING_TABS.map((tab) => [tab.key, emptyState(tab)]));
    }
  },
  methods: {
    currentTab() {
      return MAPPING_TABS.find((tab) => tab.key === this.activeTab);
    },
    // Tiap dibuka: tab aktif dimuat (sekali), filter awal Material Type disesuaikan dengan item yang sedang dipilih.
    onShow() {
      MAPPING_TABS.forEach((tab) => { if (this.state[tab.key].loaded) this.applyContextFilter(tab); });
      this.load(this.currentTab());
    },
    partLabel(part) {
      return PART_LABELS[part] || part;
    },
    targetColumns(tab) {
      return tab.targets || ["options"];
    },
    // Kunci source "ZSPR_A001" -> { material_type: "ZSPR", plant: "A001" }. Jumlah bagian TIDAK selalu sama (MRP_CONFIG ada
    // "ZSUP_050103_A001" dan "050903_A001" tanpa Material Type; Profit Center ZNOV tanpa Valuation Class), jadi tiap bagian
    // dikenali dari bentuknya (SOURCE_PART_PATTERNS), bukan dari posisinya. Bagian yang tidak dikenali -> Plant kalau tab punya
    // Plant, selain itu bagian pertama yang masih kosong. Bagian yang tidak ada di kunci dibiarkan kosong.
    splitSource(tab, source) {
      const parts = tab.sourceParts;
      const text = String(source ?? "");
      if (parts.length === 1) return { [parts[0]]: text };
      const result = Object.fromEntries(parts.map((part) => [part, ""]));
      text.split("_").filter((piece) => piece !== "").forEach((piece) => {
        const free = (part) => parts.includes(part) && result[part] === "";
        let part = Object.keys(SOURCE_PART_PATTERNS).find((name) => SOURCE_PART_PATTERNS[name].test(piece) && free(name));
        if (!part && free("plant")) part = "plant";
        if (!part) part = parts.find((name) => result[name] === "");
        if (part) result[part] = piece;
      });
      return result;
    },
    toRow(tab, record) {
      const row = { ...this.splitSource(tab, record.source) };
      const target = record.target;
      if (tab.targets) {
        tab.targets.forEach((key) => { row[key] = target?.[key] ?? ""; });
      } else {
        row.options = Array.isArray(target) ? target : [];
      }
      row._search = [record.source, ...Object.values(row).flat()].join(" ").toLowerCase();
      return row;
    },
    async load(tab, force = false) {
      const tabState = this.state[tab.key];
      if (tabState.loading || (tabState.loaded && !force)) return;
      tabState.loading = true;
      tabState.error = false;
      try {
        const response = await api(this, formConfigService.getConfigDataMapping({
          exact: 1, company: this.company, mapping_type: tab.key
        }), false);
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.data || []);
        tabState.rows = list.map((record) => this.toRow(tab, record))
          .sort((a, b) => tab.sourceParts.map((p) => String(a[p]).localeCompare(String(b[p]), undefined, { numeric: true })).find((c) => c !== 0) || 0);
        tabState.loaded = true;
        this.applyContextFilter(tab);
      } catch (error) {
        tabState.error = true;
      } finally {
        tabState.loading = false;
      }
    },
    // Filter awal Material Type = material type item terpilih (kalau ada di data tab itu).
    applyContextFilter(tab) {
      const tabState = this.state[tab.key];
      if (!this.contextMaterialType || !("material_type" in tabState.filters)) return;
      if (tabState.rows.some((row) => row.material_type === this.contextMaterialType)) {
        tabState.filters.material_type = [this.contextMaterialType];
      }
    },
    distinctValues(tab, part) {
      return [...new Set(this.state[tab.key].rows.map((row) => row[part]).filter(Boolean))]
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    },
    filteredRows(tab) {
      const tabState = this.state[tab.key];
      const search = tabState.search.trim().toLowerCase();
      return tabState.rows.filter((row) =>
        Object.entries(tabState.filters).every(([part, values]) => !values?.length || values.includes(row[part]))
        && (!search || row._search.includes(search)));
    },
    hasActiveFilter(tab) {
      const tabState = this.state[tab.key];
      return !!tabState.search || Object.values(tabState.filters).some((values) => values?.length);
    },
    resetFilters(tab) {
      const tabState = this.state[tab.key];
      tabState.search = "";
      Object.keys(tabState.filters).forEach((part) => { tabState.filters[part] = []; });
    },
    // Matriks bagian source pertama (baris) x kedua (kolom), sel = daftar target.
    matrix(tab) {
      const [rowPart, colPart] = tab.sourceParts;
      const rows = this.filteredRows(tab);
      const cells = {};
      rows.forEach((row) => { cells[`${row[rowPart]}|${row[colPart]}`] = row.options; });
      const sorted = (values) => [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
      return { rows: sorted(rows.map((row) => row[rowPart])), cols: sorted(rows.map((row) => row[colPart])), cells };
    },
    pillStyle(value) {
      const text = String(value ?? "");
      let hash = 0;
      for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
      const [background, color] = PILL_COLORS[hash % PILL_COLORS.length];
      return { background, color };
    }
  }
};
</script>

<style scoped>
.mapping-ref-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8f1fb;
  color: #185ea2;
}
.mapping-ref-count {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  background: #eef2f6;
  color: #475467;
}
.mapping-ref-card {
  box-shadow: none !important;
  border: 1px solid #e5e7eb;
}
.mapping-ref-explain {
  background: #f8fafc;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  padding: 10px 12px;
}
.mapping-ref-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #667085;
}
.mapping-ref-chip {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
}
.mapping-ref-chip.is-source {
  background: #eef2f6;
  color: #344054;
}
.mapping-ref-chip.is-target {
  background: #e8f1fb;
  color: #185ea2;
}
.mapping-ref-search {
  min-width: 240px;
  flex: 1 1 240px;
  max-width: 320px;
}
.mapping-ref-filter {
  min-width: 170px;
}
.mapping-ref-context {
  font-size: 0.8rem;
  color: #185ea2;
  background: #f0f6fd;
  border-radius: 6px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.mapping-ref-empty {
  text-align: center;
  padding: 40px 0;
  color: #667085;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.mapping-ref-empty i {
  font-size: 1.8rem;
  color: #98a2b3;
}
.mapping-ref-pill {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}
.mapping-ref-matrix-wrap {
  overflow: auto;
  max-height: 50vh;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.mapping-ref-matrix {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 0.8rem;
}
.mapping-ref-matrix th,
.mapping-ref-matrix td {
  border-bottom: 1px solid #eef0f3;
  border-right: 1px solid #eef0f3;
  padding: 8px 10px;
  vertical-align: top;
}
.mapping-ref-matrix thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  font-weight: 700;
  color: #344054;
  white-space: nowrap;
}
.mapping-ref-matrix tbody th {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  font-weight: 700;
  color: #344054;
  white-space: nowrap;
}
.mapping-ref-matrix thead th.is-corner {
  left: 0;
  z-index: 3;
  color: #667085;
  font-weight: 600;
}
.mapping-ref-matrix tbody tr:hover td,
.mapping-ref-matrix tbody tr:hover th {
  background: #f9fbfd;
}
.mapping-ref-table :deep(th.is-source) {
  background: #f2f4f7 !important;
}
.mapping-ref-table :deep(th.is-target) {
  background: #e8f1fb !important;
  color: #185ea2;
}
.mapping-ref-table :deep(td.is-target-body) {
  background: rgba(232, 241, 251, 0.25);
}
</style>
